import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const DISMISS_KEY = "b94_push_dismissed_until";
const HIDE_DAYS = 3;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function isSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Small dismissible banner that offers to enable push notifications for
 * the authenticated user. Mount inside authenticated dashboards.
 */
export default function NotificationPrompt() {
  const [state, setState] = useState("checking"); // checking | offer | subscribed | denied | hidden
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSupported()) {
      setState("hidden");
      return;
    }
    if (Notification.permission === "denied") {
      setState("denied");
      return;
    }
    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (existing) {
          setState("subscribed");
          return;
        }
      } catch (_e) { /* ignore */ }
      if (Date.now() < dismissedUntil) {
        setState("hidden");
      } else {
        setState("offer");
      }
    })();
  }, []);

  const enable = async () => {
    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast.info("Permiso de notificaciones no concedido");
        setState(permission === "denied" ? "denied" : "hidden");
        return;
      }
      const { data: keyData } = await api.get("/push/vapid-public-key");
      const publicKey = keyData?.public_key;
      if (!publicKey) throw new Error("VAPID public key missing");
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      await api.post("/push/subscribe", subscription.toJSON());
      setState("subscribed");
      toast.success("Notificaciones activadas");
    } catch (e) {
      console.error("push subscribe failed", e);
      toast.error("No se pudieron activar las notificaciones");
      setState("hidden");
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
      setState("offer");
      toast.success("Notificaciones desactivadas");
    } catch (e) {
      console.error("push unsubscribe failed", e);
      toast.error("No se pudieron desactivar las notificaciones");
    } finally {
      setLoading(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + HIDE_DAYS * 24 * 3600 * 1000));
    setState("hidden");
  };

  if (state === "checking" || state === "hidden") return null;

  if (state === "subscribed") {
    return (
      <button
        onClick={disable}
        disabled={loading}
        data-testid="push-disable-btn"
        title="Notificaciones activas — clic para desactivar"
        className="fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full bg-emerald-600/90 hover:bg-emerald-500 text-white text-xs font-medium px-3 py-2 shadow-lg border border-emerald-400/40 backdrop-blur"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">Notificaciones activas</span>
      </button>
    );
  }

  if (state === "denied") {
    return (
      <div
        data-testid="push-denied-banner"
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-40 bg-zinc-900 border border-red-500/40 rounded-xl shadow-2xl p-3 flex items-start gap-2"
      >
        <BellOff className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
        <div className="text-xs text-zinc-300 flex-1">
          Notificaciones bloqueadas. Habilítalas desde la configuración del navegador para recibir alertas de pedidos.
        </div>
        <button onClick={dismiss} data-testid="push-denied-dismiss" className="text-zinc-500 hover:text-white">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // offer
  return (
    <div
      data-testid="push-offer-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-40 bg-zinc-900 border border-yellow-400/40 rounded-xl shadow-2xl p-3"
    >
      <button
        onClick={dismiss}
        data-testid="push-offer-dismiss"
        aria-label="Cerrar"
        className="absolute top-2 right-2 text-zinc-500 hover:text-white p-1"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-9 h-9 rounded-lg bg-yellow-400 flex items-center justify-center shrink-0">
          <Bell className="w-4 h-4 text-black" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-sm text-white">Activa notificaciones</div>
          <div className="text-xs text-zinc-400 mt-0.5">
            Recibe alertas de pedidos, pagos y despachos aunque no tengas el portal abierto.
          </div>
          <button
            onClick={enable}
            disabled={loading}
            data-testid="push-enable-btn"
            className="mt-2.5 w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-black font-bold text-xs py-2 rounded-md transition-colors flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
            {loading ? "Activando..." : "Activar"}
          </button>
        </div>
      </div>
    </div>
  );
}
